import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  INTAKE_CANNOT_RECORD,
  ORDER_TYPE_LABELS,
  useCreateClient,
  type AppointingOrderType,
  type NewClientInput,
} from '../api/newClient.ts';
import { ApiError } from '../api/client.ts';
import { useResidenceTypes, type FacilityOption } from '../api/places.ts';
import { FacilityPicker, HomeFields, EMPTY_HOME, homeInput, type HomeDraft } from '../components/PlacePicker.tsx';

/** Today as a calendar day in the viewer's own zone — never via toISOString,
 *  which rolls over at 8pm in New York. Same reason as RecordVisit. */
function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';

const CATEGORIES = [
  'Elderly/Incapacitated Adult',
  'Developmentally Disabled Adult',
  'Minor',
] as const;

const COUNTIES = [
  ['BRONX', 'Bronx'],
  ['KINGS', 'Kings'],
  ['NEW_YORK', 'New York'],
  ['QUEENS', 'Queens'],
  ['RICHMOND', 'Richmond'],
  ['NASSAU', 'Nassau'],
  ['SUFFOLK', 'Suffolk'],
] as const;

const ORDER_TYPES = (
  Object.keys(ORDER_TYPE_LABELS) as AppointingOrderType[]
).filter((t) => !INTAKE_CANNOT_RECORD.includes(t));

/**
 * Add a client.
 *
 * **Two kinds of client, one screen.** The first question is whether the agency
 * has been appointed, because everything after it differs: a person the agency
 * is not yet guardian for gets an X reference and a Pipeline status, and an
 * appointed client gets a numbered reference and a status the server derives
 * from whether the order is temporary. Neither the reference nor the status is
 * ever sent from here — the server allocates one under a lock and derives the
 * other — so this form cannot disagree with the database about either.
 *
 * **Why the radar date is required and says so.** `v_visit_progress` counts a
 * quarter when the radar date is null, for every quarter back to 2020. A client
 * created without one does not sit quietly doing nothing; they appear years
 * behind on visits, indistinguishable from a real failure of care. That is the
 * one field whose absence makes a false client rather than an incomplete one,
 * and the form explains it rather than marking it required and moving on.
 *
 * **Why an appointed client must carry an order.** Authority comes from the
 * order, not from the client record: without one the agency holds no side, no
 * Care Plan or Initial Visit deadline is generated, and the scope stays blank.
 * The alternative to asking here is a client who exists and owes nothing, which
 * is the failure this screen was asked to make impossible.
 *
 * A co-guardian appointment is deliberately not offered. It needs the other
 * guardian on record, and recording it as sole authority would overstate what
 * the agency holds on the day the client is created.
 */
export function AddClient() {
  const navigate = useNavigate();
  const create = useCreateClient();

  const [appointed, setAppointed] = useState<boolean | null>(null);

  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [dob, setDob] = useState('');
  const [category, setCategory] = useState('');
  const [radarDate, setRadarDate] = useState(todayLocal());
  const [radarEstimated, setRadarEstimated] = useState(false);

  const [countyCode, setCountyCode] = useState('');
  const [orderType, setOrderType] =
    useState<AppointingOrderType>('ORDER_AND_JUDGMENT_APPOINTING');
  const [signedDate, setSignedDate] = useState('');
  const [grantsPerson, setGrantsPerson] = useState(true);
  const [grantsProperty, setGrantsProperty] = useState(false);
  const [indexNumber, setIndexNumber] = useState('');
  const [orderIsCommission, setOrderIsCommission] = useState<boolean | null>(null);
  const [filingMethod, setFilingMethod] = useState<'' | 'IN_PERSON' | 'ONLINE'>('');

  // Where they live: a building, their own home, or not recorded yet. The kind
  // of placement is never asked here; it follows from the building (061).
  const [livesIn, setLivesIn] = useState<'' | 'facility' | 'home'>('');
  const [facility, setFacility] = useState<FacilityOption | null>(null);
  const [home, setHome] = useState<HomeDraft>(EMPTY_HOME);
  const newHome = livesIn === 'home' ? homeInput(home) : null;
  // The placement gate, asked of the server: placing a client is not the same
  // right as adding one (Arthur, 23 September). The route refuses regardless.
  const placeRights = useResidenceTypes('facility');
  const mayPlace = placeRights.data?.may_add === true;

  // A temporary order appoints a temporary guardian; everything else is
  // permanent. The server derives AIP or IP from this, and the form shows what
  // it will be rather than asking for a status nobody should be typing.
  const isTemporary = orderType === 'TEMPORARY_GUARDIAN_APPOINTED';
  const derivedStatus = isTemporary ? 'AIP' : 'IP';

  // The Short Form Order is the commission. Offered as the default answer to
  // the intake question, not as the answer — it confirms, it does not decide.
  const suggestsCommission = orderType === 'SHORT_FORM_ORDER';

  /**
   * One intention, one request — the same ref as RecordVisit, set synchronously
   * so a second click in the same tick never becomes a second request.
   *
   * **It is a button, and a button is not a guarantee.** Allocation is
   * serialised in the database, so two submissions cannot collide on a
   * reference — but they would receive two different ones and create two
   * clients, and there is no natural key to refuse the second: two clients
   * genuinely share a name here, and the date of birth is optional. Preventing
   * that properly needs an idempotency key, which is a decision rather than
   * something this form should invent.
   */
  const sent = useRef(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (sent.current || appointed === null) return;
    sent.current = true;

    const common = {
      last_name: lastName.trim(),
      ...(firstName.trim() ? { first_name: firstName.trim() } : {}),
      ...(dob ? { date_of_birth: dob } : {}),
      ...(category ? { client_category: category } : {}),
      radar_date: radarDate,
      radar_date_estimated: radarEstimated,
      ...(livesIn === 'facility' && facility
        ? { placement: { facility_id: facility.id, started_on: radarDate } }
        : livesIn === 'home' && newHome
          ? { placement: { home: newHome, started_on: radarDate } }
          : {}),
    };

    const input: NewClientInput = appointed
      ? {
          appointed: true,
          ...common,
          county_code: countyCode,
          order: {
            order_type_code: orderType,
            signed_date: signedDate,
            grants_person: grantsPerson,
            grants_property: grantsProperty,
            is_temporary: isTemporary,
            ...(indexNumber.trim()
              ? { court_index_number: indexNumber.trim() }
              : {}),
          },
          ...(orderIsCommission !== null
            ? { order_is_commission: orderIsCommission }
            : {}),
          ...(filingMethod ? { filing_method: filingMethod } : {}),
        }
      : { appointed: false, ...common };

    create.mutate(input, {
      // replace, so back from the new client's record does not return to a
      // filled-in form that has already been submitted.
      onSuccess: (made) =>
        navigate(`/clients/${made.client.id}`, {
          replace: true,
          state: { recorded: `${made.client.client_ref} added.` },
        }),
      onError: () => {
        sent.current = false;
      },
    });
  };

  // A placement half-filled is not sent half-filled, and never silently
  // dropped: finish it, or say it is not recorded yet.
  const placementReady =
    livesIn === '' ||
    (livesIn === 'facility' && facility !== null && facility.placement_kind_label !== null) ||
    (livesIn === 'home' && newHome !== null);

  const canSubmit =
    appointed !== null &&
    lastName.trim().length > 0 &&
    radarDate.length === 10 &&
    placementReady &&
    (!appointed ||
      (countyCode.length > 0 &&
        signedDate.length === 10 &&
        (grantsPerson || grantsProperty)));

  return (
    <div className="mx-auto max-w-3xl bg-white min-h-dvh">
      <header className="sticky top-0 z-10 border-b border-neutral-200 bg-white px-4 py-3">
        <p className="text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase">
          Intake
        </p>
        <h1 className="mt-0.5 text-xl leading-tight font-bold text-neutral-900">
          Add a client
        </h1>
      </header>

      <form onSubmit={submit} className="space-y-6 px-4 py-4">
        {/* The discriminator. Asked first because it changes everything after. */}
        <fieldset>
          <legend className={label}>
            Has the agency been appointed for this person?
          </legend>
          <p className={hint}>
            Before an order is signed there is only a notification, so the client
            is held in the pipeline with an X reference.
          </p>
          <div className="mt-2 space-y-2">
            {([
              [true, 'Yes — there is a signed order',
               'They get a numbered reference, and their legal status follows the order.'],
              [false, 'Not yet',
               'They get an X reference and sit in the pipeline until the order arrives.'],
            ] as const).map(([value, title, sub]) => (
              <label
                key={String(value)}
                className={
                  'flex min-h-[60px] w-full items-start gap-3 rounded-lg border px-4 py-3 ' +
                  (appointed === value
                    ? 'border-neutral-900 bg-neutral-50'
                    : 'border-neutral-300 bg-white')
                }
              >
                <input
                  type="radio" name="appointed" className="mt-1 size-5 shrink-0"
                  checked={appointed === value}
                  onChange={() => setAppointed(value)}
                />
                <span className="min-w-0">
                  <span className="block font-semibold text-neutral-900">{title}</span>
                  <span className="block text-sm text-neutral-600">{sub}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={label} htmlFor="last-name">Surname</label>
            <input
              id="last-name" required value={lastName}
              onChange={(e) => setLastName(e.target.value)} className={field}
            />
            <p className={hint}>
              Both surnames where applicable, paternal first. Other names go in
              aliases, never in this field.
            </p>
          </div>
          <div>
            <label className={label} htmlFor="first-name">First name</label>
            <input
              id="first-name" value={firstName}
              onChange={(e) => setFirstName(e.target.value)} className={field}
            />
          </div>
          <div>
            <label className={label} htmlFor="dob">Date of birth</label>
            <input
              id="dob" type="date" max={todayLocal()} value={dob}
              onChange={(e) => setDob(e.target.value)} className={field}
            />
          </div>
          <div>
            <label className={label} htmlFor="category">Category</label>
            <select
              id="category" value={category}
              onChange={(e) => setCategory(e.target.value)} className={field}
            >
              <option value="">Not recorded</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={label} htmlFor="radar-date">
            When did this person come to the agency&rsquo;s attention?
          </label>
          <input
            id="radar-date" type="date" required max={todayLocal()}
            value={radarDate} onChange={(e) => setRadarDate(e.target.value)}
            className={field}
          />
          <p className={hint}>
            Required. Visits are owed from this date onwards — without it the
            client is counted as owing visits from 2020 and reads as years
            behind.
          </p>
          <label className="mt-2 flex min-h-[44px] items-center gap-3 text-[0.9375rem] text-neutral-700">
            <input
              type="checkbox" className="size-5" checked={radarEstimated}
              onChange={(e) => setRadarEstimated(e.target.checked)}
            />
            This date is an estimate
          </label>
        </div>

        {appointed === true && (
          <section className="space-y-4 rounded-lg border border-neutral-200 p-4">
            <h2 className="font-semibold text-neutral-900">The appointing order</h2>

            <div>
              <label className={label} htmlFor="order-type">Order</label>
              <select
                id="order-type" value={orderType}
                onChange={(e) => setOrderType(e.target.value as AppointingOrderType)}
                className={field}
              >
                {ORDER_TYPES.map((t) => (
                  <option key={t} value={t}>{ORDER_TYPE_LABELS[t]}</option>
                ))}
              </select>
              <p className={hint}>
                This order makes them <strong>{derivedStatus}</strong>
                {isTemporary
                  ? ' — the court has found reasonable cause but has not yet determined capacity.'
                  : ' — it follows a finding that the client lacks capacity.'}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={label} htmlFor="signed">Signed on</label>
                <input
                  id="signed" type="date" required max={todayLocal()}
                  value={signedDate} onChange={(e) => setSignedDate(e.target.value)}
                  className={field}
                />
              </div>
              <div>
                <label className={label} htmlFor="county">County of the proceeding</label>
                <select
                  id="county" required value={countyCode}
                  onChange={(e) => setCountyCode(e.target.value)} className={field}
                >
                  <option value="">Choose…</option>
                  {COUNTIES.map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </div>
            </div>

            <fieldset>
              <legend className={label}>What does it grant?</legend>
              <p className={hint}>
                At least one. This is what gives the agency authority — an order
                granting neither leaves the client owing nothing.
              </p>
              <div className="mt-2 space-y-2">
                {([
                  ['person', grantsPerson, setGrantsPerson, 'Personal needs'],
                  ['property', grantsProperty, setGrantsProperty, 'Property management'],
                ] as const).map(([key, value, set, text]) => (
                  <label
                    key={key}
                    className="flex min-h-[48px] items-center gap-3 rounded-lg border
                               border-neutral-300 px-4 text-[0.9375rem] text-neutral-800"
                  >
                    <input
                      type="checkbox" className="size-5" checked={value}
                      onChange={(e) => set(e.target.checked)}
                    />
                    {text}
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <label className={label} htmlFor="index">Court index number</label>
              <input
                id="index" value={indexNumber}
                onChange={(e) => setIndexNumber(e.target.value)} className={field}
              />
            </div>

            <div>
              <label className={label} htmlFor="is-commission">
                Does the order serve as the commission?
              </label>
              <select
                id="is-commission"
                value={orderIsCommission === null ? '' : String(orderIsCommission)}
                onChange={(e) =>
                  setOrderIsCommission(
                    e.target.value === '' ? null : e.target.value === 'true',
                  )
                }
                className={field}
              >
                <option value="">Not answered</option>
                <option value="true">Yes — the order is the commission</option>
                <option value="false">No — a commission must be obtained</option>
              </select>
              {suggestsCommission && orderIsCommission === null && (
                <p className={hint}>
                  A Short Form Order usually is the commission. Confirm it rather
                  than leaving it unanswered — an unanswered commission counts as
                  outstanding.
                </p>
              )}
            </div>

            <div>
              <label className={label} htmlFor="filing">
                How will the commission be asked for?
              </label>
              <select
                id="filing" value={filingMethod}
                onChange={(e) =>
                  setFilingMethod(e.target.value as '' | 'IN_PERSON' | 'ONLINE')
                }
                className={field}
              >
                <option value="">
                  Use the default for the county
                  {countyCode === 'NEW_YORK' ? ' (in person)' : countyCode ? ' (online)' : ''}
                </option>
                <option value="IN_PERSON">In person</option>
                <option value="ONLINE">Online</option>
              </select>
              <p className={hint}>
                New York County is asked in person, everywhere else online.
                Choosing here records that the default was overridden.
              </p>
            </div>
          </section>
        )}

        <section className="space-y-4 rounded-lg border border-neutral-200 p-4">
          <h2 className="font-semibold text-neutral-900">Where they live (optional)</h2>
          <p className="text-sm text-neutral-600">
            Recording this now sets the visiting standard, which follows the kind
            of place they live in. Without it the standard is the one for an
            unrecorded residence — they still owe visits.
          </p>

          {!mayPlace ? (
            <p className={hint}>
              {placeRights.isPending
                ? 'Checking whether you can record this…'
                : 'Recording where they live is for a case manager on the client, the Director of Social Work or the Paralegal. Leave it for them; the client is added without it.'}
            </p>
          ) : (
          <>
          <div className="flex flex-wrap gap-2">
            {([['facility', 'In a facility'], ['home', 'In their own home'], ['', 'Not recorded yet']] as const).map(
              ([v, text]) => (
                <button
                  key={v || 'none'} type="button" onClick={() => setLivesIn(v)}
                  className={`min-h-[44px] rounded-lg border px-3 text-sm font-semibold ${
                    livesIn === v
                      ? 'border-neutral-900 bg-neutral-900 text-white'
                      : 'border-neutral-300 text-neutral-800 hover:bg-neutral-50'}`}
                >
                  {text}
                </button>
              ),
            )}
          </div>

          {livesIn === 'facility' && (
            <>
              <FacilityPicker id="facility-q" selected={facility} onSelect={setFacility} />
              {/* The kind is shown, not asked: intake works from an order naming
                  a building, and the building already says what it is. */}
              {facility && (
                <p className={hint}>
                  {facility.placement_kind_label
                    ? <>Recorded as a <strong>{facility.placement_kind_label}</strong> placement, from the building&rsquo;s type.</>
                    : <>This building has no type recorded, so the placement cannot be recorded until someone records what it is.</>}
                </p>
              )}
            </>
          )}

          {livesIn === 'home' && (
            <HomeFields
              draft={home} onChange={setHome}
              ownerName={[firstName.trim(), lastName.trim()].filter(Boolean).join(' ') || null}
            />
          )}
          </>
          )}
        </section>

        {create.error && (
          <div role="alert" className="rounded-lg border-l-8 border-critical-edge
                                       bg-critical-bg px-4 py-3 text-critical-fg">
            <p className="font-semibold">{create.error.message}</p>
            {create.error instanceof ApiError && create.error.hint && (
              <p className="mt-1 text-[0.9375rem] opacity-95">{create.error.hint}</p>
            )}
          </div>
        )}

        <div className="flex gap-3 pb-8">
          <button
            type="submit" disabled={create.isPending || !canSubmit}
            className="min-h-[52px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold
                       text-white active:bg-neutral-700 disabled:opacity-50"
          >
            {create.isPending ? 'Adding…' : 'Add client'}
          </button>
          <button
            type="button" onClick={() => navigate('/clients')}
            className="min-h-[52px] rounded-lg border border-neutral-300 px-4
                       font-semibold text-neutral-700 active:bg-neutral-100"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
